import { ActionButtons } from '../../../ActionButtons/ActionButtons';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { StepWrapper } from '../../../../../components/styled/StepWrapper';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from '../../../../../store/rootReducer';
import { handleSearch } from '../../../../../store/reducers/appointment/actions';
import { Checkbox, IconButton } from '@mui/material';
import { TextField } from '../../../../../components/formControls/TextFieldStyled/TextField';
import { InfoOutlined, Search } from '@mui/icons-material';
import { TArgCallback } from '../../../../../types/types';
import { TScreen } from '../../../../../types/screens';
import ReactGA from 'react-ga4';
import { IServiceRequest } from '../../../../../store/reducers/serviceRequests/types';
import { EServiceCategoryType } from '../../../../../store/reducers/categories/types';
import AskAddService from '../../../../../components/modals/booking/AskAddService/AskAddService';
import ServiceComment from '../../../../../components/modals/booking/ServiceComment/ServiceComment';
import {
  checkCarIsValid,
  selectCategories,
  setAdditionalServicesChosen,
  updateSelectedServiceRequests,
} from '../../../../../store/reducers/appointmentFrameReducer/actions';
import { TServiceCategory } from '../../../../../store/reducers/appointmentFrameReducer/types';
import { Caption } from '../../../../../components/wrappers/Caption/Caption';
import { useTranslation } from 'react-i18next';
import { EServiceCategoryPage } from '../../../../../api/types';
import { checkPodChanged } from '../../../../../store/reducers/appointments/actions';
import { getSortedRequests } from './utils';
import {
  Code,
  CodesWrapper,
  CodeWrapper,
  Price,
  PricesWrapper,
  SearchInput,
  Wrapper,
  MessageIconWrapper,
  DescriptionWrapper,
  PriceCommentWrapper,
  TextFieldWrapper,
  RemainingCharactersWrapper,
} from './styles';
import { useModal } from '../../../../../hooks/useModal/useModal';
import { useDebounce } from '../../../../../hooks/useDebounce/useDebounce';
import { useException } from '../../../../../hooks/useException/useException';
import { ReactComponent as MessageIcon } from '../../../../../assets/img/comment_icon.svg';
import { ReactComponent as MessageIconFilled } from '../../../../../assets/img/comment_icon_filled.svg';
import { SystemIntegrationType } from '../../../../../store/reducers/serviceCenters/types';

type TProps = {
  handleSetScreen: TArgCallback<TScreen>;
  onAddServices?: () => void;
  page: EServiceCategoryPage;
  isManagingFlow?: boolean;
};

const MAX_COUNT_WORDS_CAPACITY = 250;

const filterAscii = (value: string): string =>
  value
    .split('')
    .filter(character => character.charCodeAt(0) <= 127)
    .join('');

const hasNonAsciiCharacters = (value: string): boolean => filterAscii(value) !== value;

const MessageIconComponent = ({ filled }: { filled: boolean }) =>
  filled ? <MessageIconFilled /> : <MessageIcon />;

export const SelectOpsCode: React.FC<TProps> = ({
  isManagingFlow,
  handleSetScreen,
  onAddServices,
  page,
}) => {
  const { selectedSR, serviceRequests, search, scProfile, selectedSRComments } = useSelector(
    ({ appointment }: RootState) => appointment
  );
  const { subService, service, serviceCategories } = useSelector(
    ({ appointmentFrame }: RootState) => appointmentFrame
  );
  const { allCategories } = useSelector(({ categories }: RootState) => categories);
  const [openedComments, setOpenedComments] = useState<number[]>([]);
  const [commentText, setCommentText] = useState<{ [key: number]: string }>({});
  const [searchInput, setSearch] = useState<string>('');
  const [opsCodesList, setOpsCodesList] = useState<IServiceRequest[]>([]);
  const [selectedOpsCodes, setSelectedOpsCodes] = useState<number[]>([]);
  const isFortellisIntegration = scProfile?.integration === SystemIntegrationType.Fortellis;

  const dispatch = useDispatch();
  const isInit = useRef(true);
  const { t } = useTranslation();
  const debouncedSearch = useDebounce(searchInput);
  const showError = useException(true);
  const {
    isOpen: isAdditionalOpen,
    onOpen: onAdditionalOpen,
    onClose: onAdditionalClose,
  } = useModal();
  const {
    isOpen: isCommentedServiceNotAdded,
    onOpen: onAddCommentedService,
    onClose: OnCloseCommentedService,
  } = useModal();
  const allRequestsHavePrice = useMemo(
    () => opsCodesList.every(el => Boolean(el.price)),
    [opsCodesList]
  );

  useEffect(() => {
    setSelectedOpsCodes(selectedSR);
  }, [selectedSR]);

  useEffect(() => {
    setCommentText(selectedSRComments);
  }, [selectedSRComments]);

  useEffect(() => {
    if (!isInit.current) {
      dispatch(handleSearch(debouncedSearch));
    }
  }, [debouncedSearch, dispatch]);

  useEffect(() => {
    if (isInit.current) {
      setSearch(search);
    }
    return () => {
      dispatch(handleSearch(''));
    };
  }, [search]);

  useEffect(() => {
    isInit.current = false;
  }, []);

  const setInitialData = useCallback(() => {
    if (
      service?.type === EServiceCategoryType.IndividualServices ||
      service?.type === EServiceCategoryType.Diagnose
    ) {
      setOpsCodesList(getSortedRequests(service.serviceRequests));
    } else if (
      subService?.type === EServiceCategoryType.IndividualServices ||
      subService?.type === EServiceCategoryType.Diagnose
    ) {
      setOpsCodesList(getSortedRequests(subService.serviceRequests));
    }
  }, [subService, service]);

  useEffect(() => {
    setInitialData();
  }, [subService, service]);

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    e.persist();
    setSearch(e.target.value);
    const value = e?.target?.value?.toLowerCase().trim();
    const initialData =
      service?.type === EServiceCategoryType.IndividualServices ||
      service?.type === EServiceCategoryType.Diagnose
        ? service.serviceRequests
        : subService?.type === EServiceCategoryType.IndividualServices ||
            subService?.type === EServiceCategoryType.Diagnose
          ? subService.serviceRequests
          : [];
    setOpsCodesList(() => {
      if (value?.length) {
        return initialData.filter(item => item.description.toLowerCase().includes(value));
      } else {
        return initialData;
      }
    });
  };

  const handleCategories = (value: string) => {
    const codeId = Number(value);
    if (!codeId) return;

    const diagnoseCategory = allCategories.find(
      item => item.type === EServiceCategoryType.Diagnose && item.page === page
    );
    const individualCategory = allCategories.find(
      item => item.type === EServiceCategoryType.IndividualServices && item.page === page
    );

    const nextSelectedCodes = selectedOpsCodes.includes(codeId)
      ? selectedOpsCodes.filter(id => id !== codeId)
      : [...selectedOpsCodes, codeId];

    // keep `serviceCategories` in sync with the codes selected on this screen:
    // a category stays selected while at least one of its ops codes is checked,
    // so re-checking a code restores its category and its `categoryId` is sent again
    const categories = [diagnoseCategory, individualCategory].reduce<TServiceCategory[]>(
      (acc, category) => {
        if (!category) return acc;
        const requestIds = category.serviceRequests.map(item => item.id);
        const hasSelectedCodes = nextSelectedCodes.some(code => requestIds.includes(code));
        const isSelectedCategory = acc.some(item => item.id === category.id);

        if (hasSelectedCodes && !isSelectedCategory) return [...acc, category];
        if (!hasSelectedCodes && isSelectedCategory) {
          return acc.filter(item => item.id !== category.id);
        }
        return acc;
      },
      [...serviceCategories]
    );

    dispatch(selectCategories(categories));
  };

  const handleSelectCode = ({ target: { value } }: React.ChangeEvent<HTMLInputElement>) => {
    handleCategories(value);
    setSelectedOpsCodes(prev => {
      return prev.includes(Number(value))
        ? prev.filter(el => el !== Number(value))
        : [...prev, Number(value)];
    });
  };

  const goNext = () => {
    handleSetScreen('maintenanceDetails');
  };

  const onCarIsValid = () => scProfile && dispatch(checkPodChanged(scProfile.id, showError));

  const handleGA = () => {
    ReactGA.event('a_la_carte_service_selected', {
      selected_codes: serviceRequests
        .filter(item => selectedOpsCodes.includes(item.id))
        .map(sr => `${sr.code} (${sr.description})`)
        .join(', '),
    });
  };

  const handleValidateCheckedServiceComments = () => {
    if (!Object.keys(commentText).every(i => selectedOpsCodes.includes(+i))) {
      onAddCommentedService();
    } else {
      confirmValidationCommentsModal();
    }
  };

  const handleValidateCheckedServiceCommentsUpdate = () => {
    if (
      Object.keys(commentText).some(
        i => !selectedOpsCodes.includes(+i) && (commentText[Number(i)]?.length ?? 0) > 0
      )
    ) {
      onAddCommentedService();
    } else {
      dispatch(updateSelectedServiceRequests(selectedOpsCodes, commentText));
      dispatch(checkCarIsValid(onCarIsValid, goNext));
    }
  };

  const onCloseValidationCommentsModal = () => {
    OnCloseCommentedService();
    const filteredComments = Object.fromEntries(
      Object.entries(commentText).filter(([key]) => selectedOpsCodes.includes(Number(key)))
    );
    dispatch(updateSelectedServiceRequests(selectedOpsCodes, filteredComments));
  };

  const onCloseValidationCommentsModalUpdate = () => {
    OnCloseCommentedService();
    const filteredComments = Object.fromEntries(
      Object.entries(commentText).filter(([key]) => selectedOpsCodes.includes(Number(key)))
    );
    dispatch(updateSelectedServiceRequests(selectedOpsCodes, filteredComments));
    dispatch(checkCarIsValid(onCarIsValid, goNext));
  };

  const confirmValidationCommentsModal = () => {
    OnCloseCommentedService();
    const newSelectedOpsCodes = Array.from(
      new Set([
        ...selectedOpsCodes,
        ...Object.keys(commentText)
          .filter(i => (commentText[Number(i)]?.length ?? 0) > 0)
          .map(i => +i),
      ])
    );
    setSelectedOpsCodes(newSelectedOpsCodes);
    dispatch(updateSelectedServiceRequests(newSelectedOpsCodes, commentText));
  };

  const confirmValidationCommentsModalUpdate = () => {
    OnCloseCommentedService();
    const newSelectedOpsCodes = Array.from(
      new Set([
        ...selectedOpsCodes,
        ...Object.keys(commentText)
          .filter(i => (commentText[Number(i)]?.length ?? 0) > 0)
          .map(i => +i),
      ])
    );
    setSelectedOpsCodes(newSelectedOpsCodes);
    dispatch(updateSelectedServiceRequests(newSelectedOpsCodes, commentText));
    dispatch(checkCarIsValid(onCarIsValid, goNext));
  };

  const handleNext = () => {
    handleGA();
    if (isManagingFlow) {
      // dispatch(checkCarIsValid(onCarIsValid, goNext));
      handleValidateCheckedServiceCommentsUpdate();
    } else {
      handleValidateCheckedServiceComments();
      onAdditionalOpen();
    }
  };

  const handleBack = () => {
    handleSetScreen('serviceNeeds');
  };

  const addServices = () => {
    dispatch(setAdditionalServicesChosen(true));
    if (onAddServices) onAddServices();
  };

  const handleYes = () => {
    onAdditionalClose();
    addServices();
  };

  const handleNo = () => {
    onAdditionalClose();
    goNext();
  };

  const handleCommentChange = (id: number) => (e: React.ChangeEvent<HTMLInputElement>) => {
    const nextValue = e.target.value;

    if (isFortellisIntegration && hasNonAsciiCharacters(nextValue)) {
      showError(t('Special characters not allowed'));
      return;
    }

    if (nextValue.length > MAX_COUNT_WORDS_CAPACITY) {
      if (isFortellisIntegration) {
        showError(t('Only 250 characters allowed'));
      }
      return;
    }
    setCommentText(prev => ({
      ...prev,
      [id]: nextValue,
    }));
  };

  const handleShowComment = (id: number) => {
    setOpenedComments(prevState => {
      if (prevState?.includes(id)) {
        return prevState.filter(i => i !== id);
      }
      return [...prevState, id];
    });
  };

  return (
    <StepWrapper>
      <Wrapper>
        {opsCodesList.length > 10 || searchInput.length ? (
          <SearchInput
            placeholder={t('Type here')}
            value={searchInput}
            fullWidth
            onChange={handleSearchChange}
            style={{ flexShrink: 0 }}
            variant="standard"
            InputProps={{
              startAdornment: (
                <IconButton size="small">
                  <Search />
                </IconButton>
              ),
              disableUnderline: true,
            }}
          />
        ) : null}
        <CodesWrapper>
          {opsCodesList.map(s => {
            return (
              <CodeWrapper opened={openedComments.includes(s.id)} key={`${s.code} ${s.id}`}>
                <DescriptionWrapper>
                  <Code
                    key={s.id}
                    label={s?.description ?? s.code}
                    sx={{
                      whiteSpace: 'normal',
                      wordBreak: 'break-word',
                    }}
                    labelPlacement={'end'}
                    value={s.id}
                    control={
                      <Checkbox
                        onChange={e => {
                          handleSelectCode(e);
                          if (openedComments.includes(s.id)) {
                            handleShowComment(s.id);
                          }
                        }}
                        value={s.id}
                        size={'small'}
                        checked={selectedOpsCodes.includes(s.id)}
                        color="primary"
                      />
                    }
                  />

                  <PriceCommentWrapper>
                    <PricesWrapper>
                      {/*todo uncomment for offer new functionality*/}
                      {/*{s.offer ? <OfferPrice style={{fontWeight: s.offer.type === EOfferType.FreeService ? 400 : 600}}>*/}
                      {/*    {getOfferString(s.offer, Boolean(scProfile?.isRoundPrice))}*/}
                      {/*</OfferPrice> : null}*/}
                      {s.price ? (
                        <Price>${scProfile?.isRoundPrice ? s.price : s.price.toFixed(2)}</Price>
                      ) : (
                        <InfoOutlined style={{ paddingRight: 8, fontSize: '2rem' }} />
                      )}
                    </PricesWrapper>
                    <MessageIconWrapper
                      opened={
                        selectedOpsCodes.includes(s.id) ||
                        openedComments.includes(s.id) ||
                        commentText[s.id]?.length > 0
                      }
                      onClick={() => handleShowComment(s.id)}
                    >
                      <MessageIconComponent filled={commentText[s.id]?.length > 0} />
                    </MessageIconWrapper>
                  </PriceCommentWrapper>
                </DescriptionWrapper>
                <TextFieldWrapper opened={openedComments.includes(s.id)}>
                  <RemainingCharactersWrapper opened={false}>
                    {commentText[s.id]?.length ?? 0} / {MAX_COUNT_WORDS_CAPACITY} characters
                  </RemainingCharactersWrapper>
                  <TextField
                    value={commentText[s.id] ?? ''}
                    onChange={handleCommentChange(s.id)}
                    fullWidth
                    multiline
                    rows={3}
                    placeholder={t('Your comment')}
                    size="small"
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        backgroundColor: '#ffffff',

                        '& fieldset': {
                          borderColor: '#e0e0e0',
                        },
                        '&:hover fieldset': {
                          borderColor: '#bdbdbd',
                        },
                        '&.Mui-focused fieldset': {
                          borderColor: '#1976d2',
                        },
                      },
                      '& .MuiInputBase-input': {
                        padding: '12px 10px',
                        fontSize: '14px',
                        lineHeight: '20px',
                      },
                    }}
                  />
                </TextFieldWrapper>
              </CodeWrapper>
            );
          })}
        </CodesWrapper>
        {allRequestsHavePrice ? null : (
          <Caption title={t('The price for the service will be quoted at the dealership')} />
        )}
      </Wrapper>
      {!isCommentedServiceNotAdded && (
        <AskAddService onSave={handleYes} onClose={handleNo} open={isAdditionalOpen} />
      )}
      {isManagingFlow ? (
        <ServiceComment
          onSave={confirmValidationCommentsModalUpdate}
          onClose={onCloseValidationCommentsModalUpdate}
          onCloseX={() => {
            OnCloseCommentedService();
            onAdditionalClose();
          }}
          open={isCommentedServiceNotAdded}
        />
      ) : (
        <ServiceComment
          onSave={confirmValidationCommentsModal}
          onClose={onCloseValidationCommentsModal}
          onCloseX={() => {
            OnCloseCommentedService();
            onAdditionalClose();
          }}
          open={isCommentedServiceNotAdded}
        />
      )}
      <ActionButtons
        onBack={handleBack}
        nextDisabled={!selectedOpsCodes.length}
        onNext={handleNext}
        nextLabel={t('Next')}
      />
    </StepWrapper>
  );
};
